import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPut,
  apiSecurePost,
  resetCsrfToken,
} from "../../api/client";
import type {
  AccountSnapshot,
  ComparisonPresetListResponse,
  ComparisonPresetResponse,
  ComparisonPresetValues,
  LoginEventsResponse,
  LoginValues,
  MessageResponse,
  NotificationSettingsResponse,
  NotificationSubscriptionResponse,
  PreferencesPatch,
  PreferencesResponse,
  ProfilePatch,
  RegisterValues,
  SecuritySessionsResponse,
} from "./types";

export async function getCurrentAccount(
  signal?: AbortSignal,
): Promise<AccountSnapshot | null> {
  try {
    return await apiGet<AccountSnapshot>("/api/v1/account/me", signal);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null;
    }
    throw error;
  }
}

export function patchPreferences(
  values: PreferencesPatch,
): Promise<PreferencesResponse> {
  return apiPatch<PreferencesResponse, PreferencesPatch>(
    "/api/v1/account/preferences",
    values,
  );
}

export async function loginAccount(
  values: LoginValues,
): Promise<AccountSnapshot> {
  const response = await apiSecurePost<AccountSnapshot, LoginValues>(
    "/api/v1/auth/login",
    values,
  );
  resetCsrfToken();
  return response;
}

export async function registerAccount(
  values: RegisterValues,
): Promise<MessageResponse> {
  const response = await apiSecurePost<MessageResponse, RegisterValues>(
    "/api/v1/auth/register",
    values,
  );
  resetCsrfToken();
  return response;
}

export async function logoutAccount(): Promise<void> {
  await apiSecurePost<{ ok: true }, Record<string, never>>(
    "/api/v1/auth/logout",
    {},
  );
  resetCsrfToken();
}

export function patchProfile(
  values: ProfilePatch,
): Promise<AccountSnapshot> {
  return apiPatch<AccountSnapshot, ProfilePatch>(
    "/api/v1/account/profile",
    values,
  );
}

export function requestEmailVerification(): Promise<MessageResponse> {
  return apiSecurePost<MessageResponse, Record<string, never>>(
    "/api/v1/auth/email/request",
    {},
  );
}

export function requestPasswordReset(email: string): Promise<MessageResponse> {
  return apiSecurePost<MessageResponse, { email: string }>(
    "/api/v1/auth/password/request",
    { email },
  );
}

export function resendRegistrationCode(email: string): Promise<MessageResponse> {
  return apiSecurePost<MessageResponse, { email: string }>(
    "/api/v1/auth/register/resend-code",
    { email },
  );
}

export function resetPassword(
  token: string,
  newPassword: string,
): Promise<MessageResponse> {
  return apiSecurePost<
    MessageResponse,
    { token: string; new_password: string }
  >("/api/v1/auth/password/reset", {
    token,
    new_password: newPassword,
  });
}

export function confirmEmail(token: string): Promise<MessageResponse> {
  return apiSecurePost<MessageResponse, { token: string }>(
    "/api/v1/auth/email/confirm",
    { token },
  );
}

export function confirmEmailCode(
  email: string,
  code: string,
): Promise<MessageResponse> {
  return apiSecurePost<MessageResponse, { email: string; code: string }>(
    "/api/v1/auth/email/confirm-code",
    { email, code },
  );
}

export function changePassword(values: {
  current_password: string;
  new_password: string;
}): Promise<MessageResponse> {
  return apiSecurePost<
    MessageResponse,
    { current_password: string; new_password: string }
  >("/api/v1/account/security/password", values);
}

export function getSecuritySessions(
  signal?: AbortSignal,
): Promise<SecuritySessionsResponse> {
  return apiGet("/api/v1/account/security/sessions", signal);
}

export function getLoginEvents(
  signal?: AbortSignal,
): Promise<LoginEventsResponse> {
  return apiGet("/api/v1/account/security/events", signal);
}

export function revokeOtherSessions(): Promise<MessageResponse> {
  return apiDelete("/api/v1/account/security/sessions/others");
}

export function getNotificationSettings(
  signal?: AbortSignal,
): Promise<NotificationSettingsResponse> {
  return apiGet("/api/v1/notifications/settings", signal);
}

export function patchNotificationSubscription(
  subscriptionId: number,
  isEnabled: boolean,
): Promise<NotificationSubscriptionResponse> {
  return apiPatch<
    NotificationSubscriptionResponse,
    { is_enabled: boolean }
  >(`/api/v1/notifications/subscriptions/${subscriptionId}`, {
    is_enabled: isEnabled,
  });
}

export function getComparisonPresets(
  signal?: AbortSignal,
): Promise<ComparisonPresetListResponse> {
  return apiGet("/api/v1/account/presets", signal);
}

export function createComparisonPreset(
  values: ComparisonPresetValues,
): Promise<ComparisonPresetResponse> {
  return apiSecurePost<ComparisonPresetResponse, ComparisonPresetValues>(
    "/api/v1/account/presets",
    values,
  );
}

export function updateComparisonPreset(
  presetId: number,
  values: ComparisonPresetValues,
): Promise<ComparisonPresetResponse> {
  return apiPut<ComparisonPresetResponse, ComparisonPresetValues>(
    `/api/v1/account/presets/${presetId}`,
    values,
  );
}

export function deleteComparisonPreset(
  presetId: number,
): Promise<{ ok: true }> {
  return apiDelete(`/api/v1/account/presets/${presetId}`);
}
